import type { ReactNode } from 'react'
import { Link, Navigate } from 'react-router'
import { getAzureSettings } from '../../lib/api'
import { describeAzureRun } from './AzureSettingsCard'
import { formatDate } from './LiveFindings'
import { agrees, personOf, useSession } from './session'
import { useApi } from './useApi'
import './live.css'

/**
 * The Cloud page's gate (Azure CSPM, slice 4).
 *
 * Where this build does not assess Azure, there is no Cloud page: the address goes to Recommendations,
 * as it did while Cloud did not exist. Off Monitor (owner, 2026-10-10: Azure is part of Monitor),
 * Azure is never contacted, so the page says that instead of a findings list whose "no open findings"
 * nobody measured. On Monitor it is the findings list.
 */
export function CloudArea({ children }: { children: ReactNode }) {
  const session = useSession()
  if (session.state === 'loading') return <div className="live-empty">Loading…</div>

  const me = personOf(session)
  if (me?.assessesAzure !== true) return <Navigate to="/recommendations" replace />

  if (me.monitoringActive !== true) {
    return (
      <div className="live-empty">
        <p>
          Azure assessment is part of the Monitor plan. On your current plan Defenssive does not
          contact Azure, so nothing here has been assessed.
        </p>
        <p>
          <Link to="/plans">See plans</Link>
        </p>
      </div>
    )
  }

  return <>{children}</>
}

/**
 * The Cloud page's own figure in its insights strip: how many Azure subscriptions Defenssive assessed,
 * and how the last Azure assessment went. Nothing until it has an answer, or if the read fails.
 */
export function AzureTile() {
  const session = useSession()
  const azure = useApi(() => getAzureSettings(), [])
  if (azure.state !== 'ready' || !agrees(session, azure.data.workspaceId)) return null
  const a = azure.data
  const enabled = a.subscriptions?.filter((s) => s.state.toLowerCase() === 'enabled').length ?? null
  return (
    <div className="fi-tile fi-static fi-area-tile" role="group" aria-label="Azure subscriptions">
      <span className="fi-label">Azure subscriptions</span>
      <span className="fi-value">{enabled ?? '—'}</span>
      <span className="fi-sub">
        {a.lastRun ? `Last assessed ${formatDate(a.lastRun.ranAt)}. ${describeAzureRun(a.lastRun.outcome, a.lastRun.subscriptionCount)}` : 'Not assessed yet.'}
      </span>
      <Link className="fi-link" to="/settings/azure">Manage in Settings</Link>
    </div>
  )
}
